import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@eriko/ui/src/components/ui/dropdown-menu';
import { cn } from '@eriko/ui/src/lib/utils';
import { NotificationBell } from '@eriko/ui/src/notifications/NotificationBell';
import { BarChart3, BookOpen, FileText, Landmark, LogOut, MessageSquare, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useLogout } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { useAuthStore } from '../stores/authStore';
import { TrialBanner } from '../components/TrialBanner';

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const { data: notifications, isLoading: notificationsLoading } = useNotifications();

  const navigation = [
    { name: t('nav.aiChat'), href: '/chat', icon: MessageSquare },
    { name: t('nav.books'), href: '/books', icon: BookOpen },
    { name: t('nav.documents'), href: '/documents', icon: FileText },
    { name: t('nav.reports'), href: '/reports', icon: BarChart3 },
    { name: t('nav.tax'), href: '/tax', icon: Landmark },
    { name: t('nav.settings'), href: '/settings', icon: Settings },
  ];

  const initials = user?.fullName
    ? user.fullName
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    : '??';

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-50">
      {/* Top Bar */}
      <header className="flex items-center justify-between border-b border-surface-200/60 px-7 py-3.5 sticky top-0 z-50 shrink-0">
        {/* Left: Brand + Org */}
        <div className="flex items-center gap-4">
          <Link to="/" className="font-serif text-[22px] tracking-tight leading-none" aria-label="Eriko">
            <img src="/assets/logo.png" alt="Eriko" className="h-9 dark:hidden" />
            <img src="/assets/logo-dark.png" alt="" aria-hidden className="h-9 hidden dark:block" />
          </Link>
        </div>

        {/* Right: Nav + Avatar */}
        <div className="flex items-center gap-5">
          <nav className="flex items-stretch gap-6 self-stretch">
            {navigation.map((item) => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== '/' && location.pathname.startsWith(item.href));

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'relative flex items-center text-[14px] transition-colors',
                    isActive
                      ? 'font-semibold text-surface-900'
                      : 'font-medium text-surface-600 hover:text-surface-900',
                  )}
                >
                  {item.name}
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute -bottom-2 left-1/2 h-0.5 w-7 -translate-x-1/2 rounded-full bg-neg"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <NotificationBell
            items={notifications?.items ?? []}
            unreadCount={notifications?.unreadCount ?? 0}
            isLoading={notificationsLoading}
            onSelect={(item) => navigate(item.href)}
            align="end"
            side="bottom"
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center text-[12px] font-semibold text-surface-700 cursor-pointer hover:border-surface-300 transition-colors">
                {initials}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-surface-900">{user?.fullName}</p>
                <p className="text-xs text-surface-400 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2 text-surface-500" strokeWidth={1.8} />
                  {t('user.settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout.mutate()}
                className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {t('user.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <TrialBanner />

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
